import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { assignPairs } from '../api/americanas';
import { capitalizeWords, formatFullName } from '../utils/nameUtils';
import 'font-awesome/css/font-awesome.min.css';
import './AmericanaPairsEditor.css';

const createRowsForCourts = (numCourts) => {
    const totalCourts = Math.max(1, Math.ceil(Number(numCourts) || 0));
    const rows = [];
    for (let court = 1; court <= totalCourts; court += 1) {
        rows.push(
            { court, pairSlot: 1, player1Id: '', guest1Id: '', player2Id: '', guest2Id: '' },
            { court, pairSlot: 2, player1Id: '', guest1Id: '', player2Id: '', guest2Id: '' },
        );
    }
    return rows;
};

const toNumberOrEmpty = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : '';
};

const regKey = (r) => (r?.playerId != null ? `p:${r.playerId}` : r?.guestId != null ? `g:${r.guestId}` : null);

const keyFromRowSide = (row, side) => {
    const pId = row?.[`player${side}Id`];
    if (pId !== '' && pId != null) return `p:${pId}`;
    const gId = row?.[`guest${side}Id`];
    if (gId !== '' && gId != null) return `g:${gId}`;
    return '';
};

const setSideFromKey = (row, side, key) => {
    if (!key) {
        row[`player${side}Id`] = '';
        row[`guest${side}Id`] = '';
        return;
    }
    const [type, raw] = String(key).split(':');
    const id = toNumberOrEmpty(raw);
    if (type === 'p') {
        row[`player${side}Id`] = id;
        row[`guest${side}Id`] = '';
    } else if (type === 'g') {
        row[`player${side}Id`] = '';
        row[`guest${side}Id`] = id;
    } else {
        row[`player${side}Id`] = '';
        row[`guest${side}Id`] = '';
    }
};

export default function AmericanaPairsEditor({
                                                 americanaId,
                                                 onSaved,
                                                 capacity,
                                                 expectedCourts,
                                                 initialPairs = [],
                                                 allowedRegistrations = [],
                                                 playersById,
                                                 disabled = false,
                                             }) {
    const desiredCourts = useMemo(() => {
        if (expectedCourts != null) return expectedCourts;
        const numericCapacity = Number(capacity);
        if (Number.isFinite(numericCapacity) && numericCapacity > 0) {
            return Math.max(1, Math.ceil(numericCapacity / 4));
        }
        return 1;
    }, [expectedCourts, capacity]);

    const [rows, setRows] = useState(() => createRowsForCourts(desiredCourts));
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (Array.isArray(initialPairs) && initialPairs.length > 0) {
            const normalizedPairs = [...initialPairs]
                .map(pair => {
                    const court = Number(pair.court);
                    const pairSlot = Number(pair.pairSlot);
                    return {
                        court: Number.isFinite(court) && court > 0 ? court : 1,
                        pairSlot: Number.isFinite(pairSlot) && pairSlot > 0 ? pairSlot : 1,
                        player1Id: toNumberOrEmpty(pair.player1Id),
                        guest1Id: toNumberOrEmpty(pair.guest1Id),
                        player2Id: toNumberOrEmpty(pair.player2Id),
                        guest2Id: toNumberOrEmpty(pair.guest2Id),
                    };
                })
                .sort((a, b) => {
                    if (a.court !== b.court) return a.court - b.court;
                    return a.pairSlot - b.pairSlot;
                });
            setRows(normalizedPairs);
        } else {
            setRows(createRowsForCourts(desiredCourts));
        }
    }, [initialPairs, desiredCourts]);

    useEffect(() => {
        setRows(prev => {
            const courtNumbers = [...new Set(prev.map(r => r.court))].sort((a, b) => a - b);
            if (courtNumbers.length === desiredCourts) {
                const shouldRenumber = courtNumbers.some((court, idx) => court !== idx + 1);
                if (!shouldRenumber) return prev;
                const renumberMap = new Map(courtNumbers.map((court, idx) => [court, idx + 1]));
                return prev.map(r => ({ ...r, court: renumberMap.get(r.court) }));
            }

            if (courtNumbers.length < desiredCourts) {
                const renumberMap = new Map(courtNumbers.map((court, idx) => [court, idx + 1]));
                const normalized = prev.map(r => ({ ...r, court: renumberMap.get(r.court) ?? r.court }));
                let nextCourt = courtNumbers.length;
                const extended = [...normalized];
                for (let count = courtNumbers.length; count < desiredCourts; count += 1) {
                    nextCourt += 1;
                    extended.push(
                        { court: nextCourt, pairSlot: 1, player1Id: '', guest1Id: '', player2Id: '', guest2Id: '' },
                        { court: nextCourt, pairSlot: 2, player1Id: '', guest1Id: '', player2Id: '', guest2Id: '' },
                    );
                }
                return extended;
            }

            const trimmedCourts = courtNumbers.slice(0, desiredCourts);
            const renumberMap = new Map(trimmedCourts.map((court, idx) => [court, idx + 1]));
            return prev
                .filter(r => renumberMap.has(r.court))
                .map(r => ({ ...r, court: renumberMap.get(r.court) }));
        });
    }, [desiredCourts]);

    const participantOptions = useMemo(() => {
        const resolvePlayer = (playerId) => {
            if (!playersById) return undefined;
            if (typeof playersById.get === 'function') return playersById.get(playerId);
            if (typeof playersById === 'object' && playersById !== null) return playersById[playerId];
            return undefined;
        };
        const safeTrim = (v) => (typeof v === 'string' ? v.trim() : '');

        const opts = [];
        for (const r of allowedRegistrations || []) {
            const key = regKey(r);
            if (!key) continue;

            if (r.playerId != null) {
                const numericId = Number(r.playerId);
                if (!Number.isFinite(numericId) || numericId <= 0) continue;
                const player = resolvePlayer(numericId) ?? r.player ?? null;
                const firstNameRaw = safeTrim(player?.name ?? r?.playerName ?? '');
                const lastNameRaw = safeTrim(player?.lastName ?? r?.playerLastName ?? '');
                const fullName = formatFullName(firstNameRaw, lastNameRaw) || capitalizeWords(firstNameRaw) || `Jugadora #${numericId}`;
                const level = player?.level ?? r?.playerLevel;
                const labelWithLevel = level != null && `${level}` !== '' ? `${fullName} (Nv ${level})` : fullName;
                opts.push({ key, id: numericId, type: 'PLAYER', label: fullName, labelWithLevel });
            } else if (r.guestId != null) {
                const numericId = Number(r.guestId);
                if (!Number.isFinite(numericId) || numericId <= 0) continue;
                const firstNameRaw = safeTrim(r?.guest?.firstName ?? '');
                const lastNameRaw = safeTrim(r?.guest?.lastName ?? '');
                const fullName = formatFullName(firstNameRaw, lastNameRaw) || capitalizeWords(firstNameRaw) || `Invitada #${numericId}`;
                const level = r?.guest?.level;
                const labelWithLevel = level != null && `${level}` !== '' ? `${fullName} (Nv ${level})` : fullName;
                opts.push({ key, id: numericId, type: 'GUEST', label: fullName, labelWithLevel });
            }
        }
        return opts.sort((a, b) => a.label.localeCompare(b.label));
    }, [allowedRegistrations, playersById]);

    const assignedKeysBySlot = useMemo(() => {
        const map = new Map();
        rows.forEach((row, rowIndex) => {
            ['1', '2'].forEach((side) => {
                const k = keyFromRowSide(row, side);
                if (k) map.set(`${rowIndex}:side${side}`, k);
            });
        });
        return map;
    }, [rows]);

    const availableOptionsFor = useCallback((rowIndex, side) => {
        const currentKey = assignedKeysBySlot.get(`${rowIndex}:side${side}`) || null;
        const excluded = new Set();
        assignedKeysBySlot.forEach((k, slotKey) => {
            if (slotKey === `${rowIndex}:side${side}`) return;
            excluded.add(k);
        });
        return participantOptions.filter((opt) => {
            if (currentKey && opt.key === currentKey) return true;
            return !excluded.has(opt.key);
        });
    }, [assignedKeysBySlot, participantOptions]);

    const onChange = (idx, side, newKey) => {
        setRows(r => {
            const copy = [...r];
            const row = { ...copy[idx] };
            setSideFromKey(row, side, newKey || '');
            copy[idx] = row;
            return copy;
        });
    };

    const validateUniqueParticipants = () => {
        const used = new Set();
        for (const r of rows) {
            const keys = [keyFromRowSide(r, '1'), keyFromRowSide(r, '2')].filter(Boolean);
            for (const k of keys) {
                if (used.has(k)) return `La persona ${k} está repetida en varias parejas.`;
                used.add(k);
            }
        }
        return null;
    };

    const save = async () => {
        setError(null);
        const valErr = validateUniqueParticipants();
        if (valErr) { setError(valErr); return; }

        for (const r of rows) {
            const lado1Ok = !!(r.player1Id !== '' || r.guest1Id !== '');
            const lado2Ok = !!(r.player2Id !== '' || r.guest2Id !== '');
            if (!r.court || !r.pairSlot) { setError(`Falta pista/slot en court ${r.court}.`); return; }
            if (!lado1Ok || !lado2Ok) { setError(`Faltan jugadores en Pista ${r.court}, Pareja ${r.pairSlot}.`); return; }
        }

        setSaving(true);
        try {
            const payload = rows.map(r => ({
                court: Number(r.court),
                pairSlot: Number(r.pairSlot),
                player1Id: r.player1Id === '' ? null : Number(r.player1Id),
                guest1Id:  r.guest1Id  === '' ? null : Number(r.guest1Id),
                player2Id: r.player2Id === '' ? null : Number(r.player2Id),
                guest2Id:  r.guest2Id  === '' ? null : Number(r.guest2Id),
            }));
            await assignPairs(americanaId, payload);
            onSaved && onSaved('manual');
        } catch (e) {
            setError(e?.response?.data?.message || 'Error guardando parejas');
        } finally {
            setSaving(false);
        }
    };

    const groupByCourt = useMemo(() => {
        const map = new Map();
        for (const r of rows) {
            if (!map.has(r.court)) map.set(r.court, []);
            map.get(r.court).push(r);
        }
        for (const [, arr] of map.entries()) {
            arr.sort((a, b) => a.pairSlot - b.pairSlot);
        }
        return map;
    }, [rows]);

    return (
        <div className="pairs-editor-container">
            <header className="editor-header">
                <h3 className="editor-title">Editor Manual de Parejas</h3>
                <span className="court-count-badge">
                    {desiredCourts} {desiredCourts === 1 ? 'Pista' : 'Pistas'}
                </span>
            </header>

            {error && (
                <div className="error-banner">
                    <i className="fa fa-exclamation-circle"></i> {error}
                </div>
            )}

            <div className="courts-grid">
                {[...groupByCourt.entries()].map(([court, pairs]) => (
                    <div key={court} className="court-card">
                        <div className="court-header">
                            <i className="fa fa-th-large"></i> Pista {court}
                        </div>

                        <div className="pairs-container">
                            {pairs.map((row) => {
                                const idx = rows.findIndex(r => r.court === row.court && r.pairSlot === row.pairSlot);
                                const key1 = keyFromRowSide(row, '1');
                                const key2 = keyFromRowSide(row, '2');

                                return (
                                    <div key={`${court}-${row.pairSlot}`} className="pair-box">
                                        <span className="pair-label">Pareja {row.pairSlot}</span>

                                        <div className="select-group">
                                            <select
                                                className="player-select"
                                                value={key1}
                                                onChange={(e) => onChange(idx, '1', e.target.value)}
                                                disabled={disabled || saving}
                                            >
                                                <option value="">-- Jugador 1 --</option>
                                                {availableOptionsFor(idx, '1').map(opt => (
                                                    <option key={opt.key} value={opt.key}>
                                                        {opt.labelWithLevel}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="select-group">
                                            <select
                                                className="player-select"
                                                value={key2}
                                                onChange={(e) => onChange(idx, '2', e.target.value)}
                                                disabled={disabled || saving}
                                            >
                                                <option value="">-- Jugador 2 --</option>
                                                {availableOptionsFor(idx, '2').map(opt => (
                                                    <option key={opt.key} value={opt.key}>
                                                        {opt.labelWithLevel}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="editor-actions">
                <button
                    className="btn-save-pairs"
                    onClick={save}
                    disabled={saving || disabled}
                >
                    {saving ? (
                        <>
                            <i className="fa fa-spinner fa-spin"></i> Guardando...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-save"></i> Guardar Distribución
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}